import { Grid } from '@mui/material';
import { ODSText, type ODSMenuListItemProps } from '@telekom-ods/react-ui-kit';
import { Form, Formik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import MonitoringMs from '../../api/monitoring';
import useApiQuery from '../../hooks/useApiQuery';
import { FormikSelect } from '../Formik/index';

import type { Template } from '@/types/monitoringTypes';

type FilterParams = Record<string, string>;

interface Params {
  [key: string]: string | undefined;
  dashboardId?: string;
}

interface QueryConfig {
  datasource: string;
  rawSql: string;
  refId: string;
  format: string;
}

interface TargetsConfig {
  queries: QueryConfig[];
}

interface DataFrame {
  data: {
    values: string[][];
  };
}

interface ApiResponse {
  results: Record<string, { frames: DataFrame[] }>;
}

const Filters = ({
  templates,
  setFilterParams,
}: {
  templates: Template[];
  setFilterParams: (params: FilterParams) => void;
}) => {
  const { dashboardId } = useParams<Params>();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuItems, setMenuItems] = useState<Record<string, ODSMenuListItemProps[]>>({});

  const { makeApiCall: getPanelDataApiCall } = useApiQuery<{ results: ApiResponse }>(
    MonitoringMs.getPanel as unknown as (data: unknown) => Promise<{ results: ApiResponse }>,
    {
      disableGlobalLoader: true,
    }
  );

  const initialValues = templates.reduce((acc, template) => {
    const searchParams = new URLSearchParams(location.search);
    const value = searchParams.get(template.name) ?? template.current.value;
    return { ...acc, [template.name]: value };
  }, {});

  const getTemplateData = useCallback(
    async (targets: TargetsConfig): Promise<void> => {
      const response = await getPanelDataApiCall(targets, dashboardId);
      const data = response?.results;

      if (data) {
        const refId = targets.queries[0]?.refId;
        const frameData = refId ? data[refId as keyof typeof data] : undefined;
        const frames = frameData?.frames as DataFrame[] | undefined;
        const firstFrame = Array.isArray(frames) && frames.length > 0 ? frames[0] : undefined;
        const values = firstFrame?.data.values[0];

        setMenuItems(prev => ({
          ...prev,
          [refId ?? '']: (values ?? []).map((item: string) => ({
            value: item,
            label: item,
          })),
        }));
      }
    },
    [getPanelDataApiCall, dashboardId]
  );

  useEffect(() => {
    templates.forEach(template => {
      getTemplateData({
        queries: [
          {
            datasource: template.datasource,
            rawSql: template.query,
            refId: template.name,
            format: 'table',
          },
        ],
      });
    });
  }, [getTemplateData, templates]);

  const handleSelectChange = (name: string, value: string): void => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set(name, value);
    const newUrl = `${location.pathname}?${newSearchParams.toString()}`;
    navigate(newUrl, { replace: true });
    setFilterParams({ [name]: value });
  };

  return (
    <Formik
      initialValues={initialValues}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSubmit={() => {}}
    >
      {({ setFieldValue }) => (
        <Form>
          <Grid container>
            <Grid
              container
              direction='column'
            >
              <Grid
                item
                paddingBottom={0.2}
              >
                <ODSText
                  data-testid='caption'
                  className='p-text'
                >
                  Filters:{' '}
                </ODSText>
              </Grid>
              <Grid item>
                <Grid
                  container
                  spacing={2}
                >
                  {templates.map(template => (
                    <Grid
                      item
                      key={template.name}
                    >
                      <FormikSelect
                        name={template.name}
                        label={template.label}
                        menuItems={menuItems[template.name] ?? []}
                        onChange={(selected: {
                          id?: string | undefined;
                          value?: string | undefined;
                          label?: string;
                        }) => {
                          setFieldValue(template.name, selected.value);
                          handleSelectChange(template.name, selected.value ?? '');
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default Filters;
